import { Injectable, OnDestroy } from '@angular/core';
import { ActionManagerEventType, IActionManager, IDocumentActionsCreator_Angular, OfficeResolvedActionDefinition } from '@office-start/action-types';
import { ContentActionGroupInfo, Document, DocumentAction, FileType } from '@office-start/document-types';
import { KnownAreas as SourceUXComponents } from '@office-start/instrumentation-types';
import { Subscription } from 'rxjs';

import { FileItem } from '../models/file-item.model';
import { KnownWorkloadReferralNames } from '../models/known-workload-referral-names.model';
import { MruFileItem } from '../mru/mru-file-item.model';
import { OfficeStartBootstrapperService } from '../office-start/office-start-bootstrapper.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ThemingService } from './theming.service';

type ToastNotificationFunction = (
    message: string,
    options?: { onClick: () => void | Promise<void>; label: string }
) => Promise<void>;

/**
 * Service for content actions 2.0.
 */
@Injectable({
    providedIn: 'root'
})
export class ContentActionsService implements OnDestroy {
    private readonly contentActionsEnabled: boolean;
    private readonly darkModeSubscription: Subscription;
    private actionManager: IActionManager;
    private contentActions: Array<OfficeResolvedActionDefinition>;
    private documentActionsCreator: IDocumentActionsCreator_Angular;
    private loadDocumentActionsCreatorPromise: Promise<boolean> = null;
    private addedToToDo: string;
    private addedToToDoFailed: string;
    private openButtonText: string;
    private createSuccessNotification: ToastNotificationFunction;
    private createErrorNotification: ToastNotificationFunction;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly themingService: ThemingService,
        private readonly officeStartBootstrapperService: OfficeStartBootstrapperService
    ) {
        this.contentActionsEnabled = this.capabilitiesService.capabilities.contentActionsEnabled && this.capabilitiesService.capabilities.contentActionsOHomeEnabled;
        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.darkModeSubscription = this.themingService.darkModeSubscription.subscribe(darkMode => {
                if (this.contentActionsEnabled && this.documentActionsCreator && typeof darkMode !== 'undefined') {
                    this.documentActionsCreator.updateActionControl(darkMode);
                }
            });
        }
    }

    public ngOnDestroy() {
        if (this.actionManager) {
            this.actionManager.removeListener(
                ActionManagerEventType.ActionsUpdated,
                this.actionsUpdatedCallback
            );
        }

        this.darkModeSubscription?.unsubscribe();
    }

    public async createToDoSuccessNotification(callToAction: () => void, callToActionLabel?: string) {
        await this.loadActionService();
        return this.createSuccessNotification(this.addedToToDo, {
            onClick: callToAction,
            label: callToActionLabel ?? this.openButtonText
        });
    }

    public async createToDoErrorNotification(errorMessage?: string) {
        await this.loadActionService();
        // Error notifications by default have only a 'Close' button
        return this.createErrorNotification(errorMessage ?? this.addedToToDoFailed);
    }

    public async createDocumentActions(
        item: MruFileItem,
        itemElementId: string,
        sourceUXComponent: SourceUXComponents,
        itemCategory: string
    ): Promise<ReadonlyArray<DocumentAction>> {
        if (!this.contentActionsEnabled || !item) {
            return [];
        }
        if (!this.documentActionsCreator || !this.actionManager || !this.contentActions) {
            if (!await this.loadActionService()) {
                return [];
            }
        }

        // Convert MruFileItem to Document
        const referralType = FileItem.referralFromFileLocation(item.url);
        const document: Document = {
            app: item.application,
            attribution: item.attribution,
            attributionUrl: item.attributionUrl,
            canonicalUrl: item.canonicalUrl,
            docId: item.docId,
            downloadUrl: item.downloadUrl,
            driveId: item.driveId,
            extension: item.extension,
            fileName: item.fileName,
            fileType: item.fileTypeName ? FileType[item.fileTypeName] : undefined,
            id: item.id,
            isDeleted: item.isDeleted,
            isEmailAttachment: item.isEmailAttachment,
            isHidden: item.isHidden,
            isLocalFile: item.isLocal,
            // MruFileItem does not have isOneDriveForBusinessFile or isSharePointFile, get the value using referralFromFileLocation
            isOneDriveForBusinessFile: referralType === KnownWorkloadReferralNames.OneDrive ? true : false,
            isSharePointFile: referralType === KnownWorkloadReferralNames.SharePoint ? true : false,
            isOnPremOrVanity: item.isOnPremOrVanity,
            isPinned: item.isPinned,
            // MruFileItem does not have isRedeemed, consider it as true as done in mru-item.components for recommended actions
            isRedeemed: item.isRedeemed ?? true,
            sharepointIds: item.sharepointIds,
            title: item.title,
            url: item.url,
            webUrl: item.webUrl
        };
        return this.documentActionsCreator.createDocumentActions(
            this.contentActions,
            document,
            itemElementId,
            sourceUXComponent,
            itemCategory
        );
    }

    public getContentActionsGroupInfo(): ContentActionGroupInfo | undefined {
        if (!this.contentActionsEnabled || !this.documentActionsCreator) {
            return undefined;
        }
        return this.documentActionsCreator.getContentActionsGroupInfo();
    }

    private actionsUpdatedCallback(): void {
        if (this.actionManager) {
            this.contentActions = this.actionManager.getContentActionsMetadata();
        }
    }

    private async loadActionService(): Promise<boolean> {
        if (this.loadDocumentActionsCreatorPromise === null) {
            this.officeStartBootstrapperService.bootstrapOfficeStart();
            this.loadDocumentActionsCreatorPromise = new Promise(resolve => {
                import('@office-start/actions-service').then(
                    async actionsService => {
                        // Bootstrap strings and actions for notifications
                        const notificationStrings = await actionsService.getNotificationStrings();
                        this.addedToToDo = notificationStrings.addedToToDo;
                        this.addedToToDoFailed = notificationStrings.addedToToDoFailed;
                        this.openButtonText = notificationStrings.openButtonText;
                        this.createSuccessNotification = actionsService.createSuccessNotification;
                        this.createErrorNotification = actionsService.createErrorNotification;

                        this.documentActionsCreator = actionsService.getDocumentActionsCreator();
                        this.actionManager = await this.documentActionsCreator.getActionConfig();
                        if (!this.actionManager) {
                            resolve(false);
                        }

                        // When new actions are added/removed/updated, update the hook 🪝
                        this.actionManager.on(
                            ActionManagerEventType.ActionsUpdated,
                            this.actionsUpdatedCallback
                        );

                        // Get initial set of actions
                        this.actionsUpdatedCallback();
                        resolve(!!this.contentActions);
                    }
                );
            });
        }
        return this.loadDocumentActionsCreatorPromise;
    }

}
